import { useEffect, useRef } from 'react';
import type { CanvasState } from './useCanvasTransform';
import type { AABB } from '../utils/aabb';
import { getHandles, hitTestHandle, type HandleType } from '../utils/handles';
import type { CanvasObject } from '../types';
import { computeAABB } from '../utils/aabb';

// 핸들 타입에 따른 커서 매핑
const handleCursorMap: Record<HandleType, string> = {
  'nw': 'nw-resize',
  'n': 'n-resize',
  'ne': 'ne-resize',
  'w': 'w-resize',
  'e': 'e-resize',
  'sw': 'sw-resize',
  's': 's-resize',
  'se': 'se-resize'
};

export function useResizeSelected(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  getCanvasState: () => CanvasState,
  getActiveTool: () => 'select' | 'rectangle' | 'circle' | 'text' | 'artboard',
  selectedId: string | null,
  getBbox: () => AABB | null,
  resizeSelected: (handle: HandleType, newX: number, newY: number, startX: number, startY: number, startWidth: number, startHeight: number, keepAspect?: boolean, aspectRatio?: number, centerBased?: boolean, targetIds?: string[], initialStates?: Map<string, { x: number; y: number; width: number; height: number }>) => void,
  requestRender: () => void,
  shouldBlock?: () => boolean,
  getObjectById?: (id: string | null) => CanvasObject | null,
  updateObject?: (id: string, updater: (obj: CanvasObject) => CanvasObject) => void,
  isResizingRef?: React.MutableRefObject<boolean>,
  onWorkComplete?: () => void,
  // 다중 선택 관련 파라미터
  selectedIds?: Set<string>,
  getSelectedObjects?: () => CanvasObject[]
) {
  const resizingRef = useRef<{ handle: HandleType; startX: number; startY: number; startWidth: number; startHeight: number } | null>(null);
  const lastPtRef = useRef<{ x: number; y: number } | null>(null);
  const startAspectRef = useRef<number | null>(null);
  // 회전 리사이징을 위한 고정 기준값
  const startWorldRef = useRef<{ x: number; y: number } | null>(null);
  const startObjWRef = useRef<number | null>(null);
  const startObjHRef = useRef<number | null>(null);
  const startCenterRef = useRef<{ x: number; y: number } | null>(null);
  // 다중 선택 리사이징을 위한 초기 상태
  const initialMultiSelectStatesRef = useRef<Map<string, { x: number; y: number; width: number; height: number }> | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const toWorld = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      const cx = clientX - rect.left;
      const cy = clientY - rect.top;
      const { scale, offset } = getCanvasState();
      return { x: (cx - offset.x) / scale, y: (cy - offset.y) / scale };
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (getActiveTool() !== 'select') return;
      if (shouldBlock && shouldBlock()) return;
      
      // 다중 선택 모드 체크
      const isMultiSelect = selectedIds && selectedIds.size > 1;
      if (!selectedId && !isMultiSelect) return;
      
      const bbox = getBbox();
      if (!bbox) return;
      const canvasEl = canvasRef.current;
      if (!canvasEl) return;
      const rect = canvasEl.getBoundingClientRect();
      // 화면 픽셀 좌표 계산
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const { scale, offset } = getCanvasState();
      const handles = getHandles(bbox, scale);
      
      // 다중 선택 모드: AABB 기준 (회전 0도)
      let rotation = 0;
      let centerX: number | undefined = bbox.x + bbox.width / 2;
      let centerY: number | undefined = bbox.y + bbox.height / 2;
      
      if (!isMultiSelect && selectedId) {
        // 단일 선택 모드: 객체의 회전과 중심 좌표 사용
        const obj = getObjectById ? getObjectById(selectedId) : null;
        rotation = obj?.rotation || 0;
        centerX = obj ? obj.x + obj.width / 2 : undefined;
        centerY = obj ? obj.y + obj.height / 2 : undefined;
      }
      
      const hitHandle = hitTestHandle(screenX, screenY, handles, scale, offset, rotation, centerX, centerY);
      if (!hitHandle) return;
      if (typeof (e as any).stopImmediatePropagation === 'function') {
        (e as any).stopImmediatePropagation();
      }
      e.stopPropagation();
      e.preventDefault();
      canvasEl.style.cursor = handleCursorMap[hitHandle];
      const p = toWorld(e.clientX, e.clientY);
      resizingRef.current = {
        handle: hitHandle,
        startX: bbox.x,
        startY: bbox.y,
        startWidth: bbox.width,
        startHeight: bbox.height
      };
      if (isResizingRef) {
        isResizingRef.current = true;
      }
      // 시작 비율 저장
      startAspectRef.current = bbox.width > 0 && bbox.height > 0 ? bbox.width / bbox.height : null;
      lastPtRef.current = p;
      // 회전 리사이징 시작 기준 저장(고정)
      startWorldRef.current = p;
      
      // 단일 선택 모드에서만 회전 리사이징을 위한 객체 정보 저장
      if (!isMultiSelect && selectedId) {
        const obj = getObjectById ? getObjectById(selectedId) : null;
        if (obj) {
          startObjWRef.current = obj.width;
          startObjHRef.current = obj.height;
          startCenterRef.current = { x: obj.x + obj.width / 2, y: obj.y + obj.height / 2 };
        } else {
          startObjWRef.current = null;
          startObjHRef.current = null;
          startCenterRef.current = null;
        }
        initialMultiSelectStatesRef.current = null;
      } else {
        // 다중 선택 모드: 각 객체의 초기 상태 저장
        if (isMultiSelect && getSelectedObjects) {
          const selectedObjs = getSelectedObjects();
          const initialStates = new Map<string, { x: number; y: number; width: number; height: number }>();
          selectedObjs.forEach(obj => {
            initialStates.set(obj.id, {
              x: obj.x,
              y: obj.y,
              width: obj.width,
              height: obj.height
            });
          });
          initialMultiSelectStatesRef.current = initialStates;
        } else {
          initialMultiSelectStatesRef.current = null;
        }
        // 다중 선택 모드에서는 회전 리사이징 사용 안 함
        startObjWRef.current = null;
        startObjHRef.current = null;
        startCenterRef.current = null;
      }
    };

    const updateCursor = (e: MouseEvent) => {
      if (resizingRef.current) {
        canvas.style.cursor = handleCursorMap[resizingRef.current.handle];
        return;
      }
      
      // 다중 선택 모드 체크
      const isMultiSelect = selectedIds && selectedIds.size > 1;
      if (getActiveTool() !== 'select' || (!selectedId && !isMultiSelect)) {
        canvas.style.cursor = 'default';
        return;
      }
      if (shouldBlock && shouldBlock()) {
        canvas.style.cursor = 'default';
        return;
      }
      const bbox = getBbox();
      if (!bbox) {
        canvas.style.cursor = 'default';
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const screenX = e.clientX - rect.left;
      const screenY = e.clientY - rect.top;
      const { scale, offset } = getCanvasState();
      const handles = getHandles(bbox, scale);
      
      // 다중 선택 모드: AABB 기준 (회전 0도)
      let rotation = 0;
      let centerX: number | undefined = bbox.x + bbox.width / 2;
      let centerY: number | undefined = bbox.y + bbox.height / 2;
      
      if (!isMultiSelect && selectedId) {
        // 단일 선택 모드: 객체의 회전과 중심 좌표 사용
        const obj = getObjectById ? getObjectById(selectedId) : null;
        rotation = obj?.rotation || 0;
        centerX = obj ? obj.x + obj.width / 2 : undefined;
        centerY = obj ? obj.y + obj.height / 2 : undefined;
      }
      
      const hitHandle = hitTestHandle(screenX, screenY, handles, scale, offset, rotation, centerX, centerY);
      if (hitHandle) {
        canvas.style.cursor = handleCursorMap[hitHandle];
      } else {
        canvas.style.cursor = 'default';
      }
    };

    const onMouseMove = (e: MouseEvent) => {
      updateCursor(e);
      if (!resizingRef.current) return;
      const p = toWorld(e.clientX, e.clientY);
      const { handle } = resizingRef.current;
      const centerBased = !!e.altKey; // Alt 키: 중앙 기준 리사이징

      // 회전 객체 처리 (local space): getObjectById/updateObject 제공 시 사용
      const obj = getObjectById ? getObjectById(selectedId) : null;
      const angle = obj?.rotation || 0;
      if (obj && updateObject && Math.abs(angle) > 1e-6 && startWorldRef.current && startCenterRef.current && startObjWRef.current && startObjHRef.current) {
        const cx0 = startCenterRef.current.x;
        const cy0 = startCenterRef.current.y;
        // 역회전 헬퍼 (월드→로컬)
        const toLocal = (wx: number, wy: number) => {
          const dx = wx - cx0;
          const dy = wy - cy0;
          const cos = Math.cos(-angle);
          const sin = Math.sin(-angle);
          return { x: dx * cos - dy * sin, y: dx * sin + dy * cos };
        };
        const startLocal = toLocal(startWorldRef.current.x, startWorldRef.current.y);
        const currLocal = toLocal(p.x, p.y);
        let dxL = currLocal.x - startLocal.x;
        let dyL = currLocal.y - startLocal.y;
        
        // 중앙 기준 리사이징 (Alt 키): 시작 핸들 위치 기준으로 이동 거리를 2배로 적용
        // (이미 startLocal을 기준으로 계산했으므로 2배만 적용)
        if (centerBased) {
          dxL *= 2;
          dyL *= 2;
        }
        
        // 단일 축 핸들은 해당 축만 반영
        switch (handle) {
          case 'w':
          case 'e':
            dyL = 0; break;
          case 'n':
          case 's':
            dxL = 0; break;
        }
        const ow = startObjWRef.current;
        const oh = startObjHRef.current;
        let sx = 1, sy = 1;
        if (handle === 'w') sx = (ow - dxL) / ow; else if (handle === 'e') sx = (ow + dxL) / ow;
        if (handle === 'n') sy = (oh - dyL) / oh; else if (handle === 's') sy = (oh + dyL) / oh;
        if (handle === 'nw') { sx = (ow - dxL) / ow; sy = (oh - dyL) / oh; }
        if (handle === 'ne') { sx = (ow + dxL) / ow; sy = (oh - dyL) / oh; }
        if (handle === 'sw') { sx = (ow - dxL) / ow; sy = (oh + dyL) / oh; }
        if (handle === 'se') { sx = (ow + dxL) / ow; sy = (oh + dyL) / oh; }
        // Shift 비율 고정
        const keepAspect = !!e.shiftKey && startAspectRef.current && startAspectRef.current > 0;
        let newW = Math.max(1, ow * sx);
        let newH = Math.max(1, oh * sy);
        if (keepAspect && startAspectRef.current) {
          const ratio = startAspectRef.current;
          if (newW / newH > ratio) newH = Math.max(1, newW / ratio); else newW = Math.max(1, newH * ratio);
        }
        // 앵커 로컬 좌표 선택 (startObjW/H 기준)
        let axOld = 0, ayOld = 0, axNew = 0, ayNew = 0;
        switch (handle) {
          case 'w': axOld = +ow/2; ayOld = 0; axNew = +newW/2; ayNew = 0; break;
          case 'e': axOld = -ow/2; ayOld = 0; axNew = -newW/2; ayNew = 0; break;
          case 'n': axOld = 0; ayOld = +oh/2; axNew = 0; ayNew = +newH/2; break;
          case 's': axOld = 0; ayOld = -oh/2; axNew = 0; ayNew = -newH/2; break;
          case 'nw': axOld = +ow/2; ayOld = +oh/2; axNew = +newW/2; ayNew = +newH/2; break;
          case 'ne': axOld = -ow/2; ayOld = +oh/2; axNew = -newW/2; ayNew = +newH/2; break;
          case 'sw': axOld = +ow/2; ayOld = -oh/2; axNew = +newW/2; ayNew = -newH/2; break;
          case 'se': axOld = -ow/2; ayOld = -oh/2; axNew = -newW/2; ayNew = -newH/2; break;
        }
        const cosR = Math.cos(angle);
        const sinR = Math.sin(angle);
        const anchorWorldX = cx0 + axOld * cosR - ayOld * sinR;
        const anchorWorldY = cy0 + axOld * sinR + ayOld * cosR;
        let cxNew = anchorWorldX - (axNew * cosR - ayNew * sinR);
        let cyNew = anchorWorldY - (axNew * sinR + ayNew * cosR);
        
        // 중앙 기준 리사이징 (Alt 키)
        if (centerBased) {
          // 중심을 고정하고 크기만 변경
          cxNew = cx0;
          cyNew = cy0;
        }
        
        // 새 박스 좌표
        const nx = Math.round(cxNew - newW / 2);
        const ny = Math.round(cyNew - newH / 2);
        const nw = Math.max(1, Math.round(newW));
        const nh = Math.max(1, Math.round(newH));
        updateObject(obj.id, (o) => ({ ...o, x: nx, y: ny, width: nw, height: nh }));
        requestRender();
        return;
      }

      // 비회전 기본 경로
      const last = lastPtRef.current;
      if (!last) { lastPtRef.current = p; return; }
      const { startX, startY, startWidth, startHeight } = resizingRef.current;
      const keepAspect = !!e.shiftKey;
      const aspect = startAspectRef.current ?? undefined;
      
      // 다중 선택 모드 체크
      const isMultiSelect = selectedIds && selectedIds.size > 1;
      if (isMultiSelect && initialMultiSelectStatesRef.current && getSelectedObjects) {
        const targetIds = Array.from(selectedIds);
        resizeSelected(handle, p.x, p.y, startX, startY, startWidth, startHeight, keepAspect, aspect, centerBased, targetIds, initialMultiSelectStatesRef.current);
      } else {
        // 단일 선택 모드
        resizeSelected(handle, p.x, p.y, startX, startY, startWidth, startHeight, keepAspect, aspect, centerBased);
      }
      
      lastPtRef.current = p;
      requestRender();
    };

    const onMouseLeave = () => {
      if (!resizingRef.current) {
        canvas.style.cursor = 'default';
      }
    };

    const onMouseUp = () => {
      if (!resizingRef.current) return;
      const wasResizing = resizingRef.current !== null;
      resizingRef.current = null;
      lastPtRef.current = null;
      startWorldRef.current = null;
      startObjWRef.current = null;
      startObjHRef.current = null;
      startCenterRef.current = null;
      initialMultiSelectStatesRef.current = null;
      canvas.style.cursor = 'default';
      if (isResizingRef) {
        isResizingRef.current = false;
      }
      // 리사이즈가 실제로 발생했으면 히스토리 저장
      if (wasResizing && onWorkComplete) {
        onWorkComplete();
      }
    };

    canvas.addEventListener('mousedown', onMouseDown, { capture: true });
    canvas.addEventListener('mousemove', updateCursor);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('mouseleave', onMouseLeave);
    return () => {
      canvas.removeEventListener('mousedown', onMouseDown, { capture: true } as any);
      canvas.removeEventListener('mousemove', updateCursor);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('mouseleave', onMouseLeave);
      canvas.style.cursor = 'default';
    };
  }, [canvasRef, getCanvasState, getActiveTool, selectedId, getBbox, resizeSelected, requestRender, shouldBlock, getObjectById, updateObject, isResizingRef, onWorkComplete, selectedIds, getSelectedObjects]);
}

